import React from "react";
import { useI18n } from "../context/I18nContext";
import NewsletterForm from "../components/site/NewsletterForm";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import {
  BarChart3,
  Database,
  LayoutDashboard,
  ShieldCheck,
  MapPin,
  UserCheck,
  ArrowRight,
} from "lucide-react";

const BENEFIT_ICONS = [
  Database,
  LayoutDashboard,
  ShieldCheck,
  UserCheck,
  BarChart3,
  MapPin,
];

const Eyebrow = ({ children }) => (
  <span
    className="inline-block text-xs font-mono uppercase tracking-[0.22em] text-emerald-700"
    style={{ fontFamily: "'JetBrains Mono', monospace" }}
  >
    {children}
  </span>
);

const H2 = ({ children }) => (
  <h2
    className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight"
    style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
  >
    {children}
  </h2>
);

export default function HomePage() {
  const { t } = useI18n();
  const hero = t("hero");
  const about = t("about");
  const how = t("how");
  const benefits = t("benefits");
  const faq = t("faq");
  const closing = t("closing");
  const newsletter = t("newsletter");

  return (
    <main
      id="main"
      style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
      className="bg-[#F8FAFC] text-slate-800"
    >
      {/* HERO */}
      <section className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12 py-16 sm:py-20 lg:py-24 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <Eyebrow>{hero.eyebrow}</Eyebrow>
            <h1
              className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] text-slate-900"
              style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
              data-testid="hero-title"
            >
              {hero.title}
            </h1>
            <p className="mt-4 text-lg sm:text-xl text-slate-700 max-w-2xl">
              {hero.subtitle}
            </p>
            <div className="mt-6 space-y-3 max-w-2xl">
              {hero.paragraphs.map((p, i) => (
                <p key={i} className="text-base text-slate-600 leading-relaxed">
                  {p}
                </p>
              ))}
            </div>
            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href="#newsletter"
                data-testid="hero-cta-primary"
                className="inline-flex items-center gap-2 rounded-md bg-slate-900 px-5 py-3 text-sm font-semibold text-white hover:bg-slate-800 no-underline"
              >
                {hero.ctaPrimary}
                <ArrowRight size={16} />
              </a>
              <a
                href="#how"
                data-testid="hero-cta-secondary"
                className="inline-flex items-center gap-2 rounded-md border border-slate-300 bg-white px-5 py-3 text-sm font-semibold text-slate-800 hover:bg-slate-50 no-underline"
              >
                {hero.ctaSecondary}
              </a>
            </div>
            <p className="mt-6 text-xs uppercase tracking-widest text-slate-500">
              {hero.trustLabel}
            </p>
          </div>
          <div className="lg:col-span-5">
            <div className="relative rounded-2xl overflow-hidden border border-slate-200 bg-slate-100">
              <img
                src="/images/quito-skyline.jpg"
                alt="Skyline of Quito, Ecuador — Sierrabienes headquarters context"
                className="w-full h-72 lg:h-[420px] object-cover"
                loading="eager"
              />
            </div>
            <div className="mt-4 grid grid-cols-3 gap-3">
              {[
                { v: hero.kpi1Value, l: hero.kpi1Label },
                { v: hero.kpi2Value, l: hero.kpi2Label },
                { v: hero.kpi3Value, l: hero.kpi3Label },
              ].map((k, i) => (
                <div
                  key={i}
                  className="rounded-lg border border-slate-200 bg-white px-3 py-4"
                >
                  <div
                    className="text-2xl font-bold text-slate-900"
                    style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
                  >
                    {k.v}
                  </div>
                  <div className="mt-1 text-xs text-slate-500 leading-tight">
                    {k.l}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ABOUT */}
      <section id="about" className="py-16 sm:py-20">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12 grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <Eyebrow>{about.eyebrow}</Eyebrow>
            <H2>
              <span className="mt-3 block">{about.title}</span>
            </H2>
            <p className="mt-4 text-lg text-slate-700">{about.subtitle}</p>
            <div className="mt-8 rounded-2xl overflow-hidden border border-slate-200">
              <img
                src="/images/dashboard-analyst.jpg"
                alt={about.imageAlt}
                className="w-full h-72 object-cover"
                loading="lazy"
              />
            </div>
          </div>
          <div className="lg:col-span-7">
            <ul className="grid gap-4">
              {about.bullets.map((b, i) => (
                <li
                  key={i}
                  className="flex gap-4 rounded-xl border border-slate-200 bg-white p-5"
                  data-testid={`about-bullet-${i}`}
                >
                  <span
                    className="mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-emerald-50 text-emerald-700 text-sm font-bold"
                    style={{ fontFamily: "'JetBrains Mono', monospace" }}
                  >
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <p className="text-slate-700 leading-relaxed">{b}</p>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* HOW */}
      <section id="how" className="py-16 sm:py-20 bg-white border-y border-slate-200">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12">
          <div className="max-w-3xl">
            <Eyebrow>{how.eyebrow}</Eyebrow>
            <H2>
              <span className="mt-3 block">{how.title}</span>
            </H2>
            <p className="mt-4 text-lg text-slate-700">{how.subtitle}</p>
          </div>
          <div className="mt-10 grid gap-6 md:grid-cols-3">
            {how.steps.map((s, i) => (
              <div
                key={i}
                className="rounded-2xl border border-slate-200 bg-[#F8FAFC] p-6 flex flex-col"
                data-testid={`how-step-${i}`}
              >
                <span
                  className="text-4xl font-bold text-emerald-700"
                  style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
                >
                  {s.n}
                </span>
                <h3
                  className="mt-3 text-xl font-semibold text-slate-900"
                  style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
                >
                  {s.title}
                </h3>
                <p className="mt-2 text-slate-600 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
          <div className="mt-10 rounded-2xl overflow-hidden border border-slate-200">
            <img
              src="/images/tablet-analytics.jpg"
              alt="Team reviewing analytics on a tablet"
              className="w-full h-72 sm:h-96 object-cover"
              loading="lazy"
            />
          </div>
        </div>
      </section>

      {/* BENEFITS */}
      <section id="benefits" className="py-16 sm:py-20">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12">
          <div className="max-w-3xl">
            <Eyebrow>{benefits.eyebrow}</Eyebrow>
            <H2>
              <span className="mt-3 block">{benefits.title}</span>
            </H2>
            <p className="mt-4 text-lg text-slate-700">{benefits.subtitle}</p>
          </div>
          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {benefits.items.map((b, i) => {
              const Icon = BENEFIT_ICONS[i % BENEFIT_ICONS.length];
              return (
                <div
                  key={i}
                  className="rounded-2xl border border-slate-200 bg-white p-6"
                  data-testid={`benefit-item-${i}`}
                >
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-md bg-slate-900 text-white">
                    <Icon size={18} />
                  </span>
                  <h3
                    className="mt-4 text-lg font-semibold text-slate-900"
                    style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
                  >
                    {b.title}
                  </h3>
                  <p className="mt-2 text-slate-600 leading-relaxed">{b.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* NEWSLETTER */}
      <section
        id="newsletter"
        className="py-16 sm:py-20 bg-[#0F172A] text-white"
      >
        <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <Eyebrow>
              <span className="text-emerald-300">{newsletter.eyebrow}</span>
            </Eyebrow>
            <h2
              className="mt-3 text-3xl sm:text-4xl font-bold tracking-tight text-white"
              style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
              data-testid="newsletter-title"
            >
              {newsletter.title}
            </h2>
            <p className="mt-4 text-slate-300 leading-relaxed">
              {newsletter.subtitle}
            </p>
            <div className="mt-8 rounded-2xl overflow-hidden border border-white/10">
              <img
                src="/images/calculator-analysis.jpg"
                alt="Analyst using a calculator and reviewing figures"
                className="w-full h-56 object-cover"
                loading="lazy"
              />
            </div>
          </div>
          <div className="lg:col-span-7">
            <NewsletterForm />
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-16 sm:py-20 bg-white">
        <div className="mx-auto max-w-4xl px-5 sm:px-8 lg:px-12">
          <div className="max-w-3xl">
            <Eyebrow>{faq.eyebrow}</Eyebrow>
            <H2>
              <span className="mt-3 block">{faq.title}</span>
            </H2>
            <p className="mt-4 text-lg text-slate-700">{faq.subtitle}</p>
          </div>
          <div className="mt-8">
            <Accordion type="single" collapsible data-testid="faq-accordion">
              {faq.items.map((item, i) => (
                <AccordionItem
                  key={i}
                  value={`faq-${i}`}
                  className="border-b border-slate-200"
                >
                  <AccordionTrigger
                    className="text-left text-base font-semibold text-slate-900 py-5"
                    data-testid={`faq-trigger-${i}`}
                  >
                    {item.q}
                  </AccordionTrigger>
                  <AccordionContent
                    className="text-slate-600 leading-relaxed pb-5"
                    data-testid={`faq-content-${i}`}
                  >
                    {item.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* CLOSING */}
      <section className="py-16 sm:py-20 bg-[#F8FAFC] border-t border-slate-200">
        <div className="mx-auto max-w-4xl px-5 sm:px-8 lg:px-12 text-center">
          <Eyebrow>{closing.eyebrow}</Eyebrow>
          <H2>
            <span className="mt-3 block">{closing.title}</span>
          </H2>
          <p className="mt-4 text-lg text-slate-700">{closing.subtitle}</p>
          <div className="mt-8">
            <a
              href="#newsletter"
              data-testid="closing-cta"
              className="inline-flex items-center gap-2 rounded-md bg-slate-900 px-6 py-3 text-sm font-semibold text-white hover:bg-slate-800 no-underline"
            >
              {closing.cta}
              <ArrowRight size={16} />
            </a>
          </div>
        </div>
      </section>
    </main>
  );
}
